/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react'
import { useParams, Link, useHistory } from 'react-router-dom'
import { AppContext } from '../context/AppContext'
import ShipmentService from '../services/ShipmentService'
import Swal from 'sweetalert2'

const ShipmentCreate = () => {
    const { currentUser } = useContext(AppContext)
    const { orderId } = useParams()
    const history = useHistory()
    const [order, setOrder] = useState(null)
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    
    // Shipment form state
    const [shipmentForm, setShipmentForm] = useState({
        orderId: orderId,
        customerId: '',
        billingAddressId: '',
        shippingAddressId: '',
        totalQty: 0,
        customerNote: '',
        items: []
    })

    useEffect(() => {
        if (orderId) {
            fetchOrderDetails()
        }
    }, [orderId])

    const fetchOrderDetails = async () => {
        setLoading(true)
        try {
            const response = await ShipmentService.getOrderDetails(orderId)
            if (response.success) {
                const orderData = response.data
                setOrder(orderData)
                
                // Initialize shipment form
                setShipmentForm({
                    orderId: orderId,
                    customerId: orderData.customerId,
                    billingAddressId: orderData.billing_address_id,
                    shippingAddressId: orderData.shipping_address_id,
                    totalQty: 0,
                    customerNote: '',
                    items: (orderData.order_item || []).map(item => ({
                        orderItemId: item.orderItemId,
                        productId: item.productId,
                        name: item.name,
                        sku: item.sku,
                        qtyOrdered: parseInt(item.qtyOrdered) || 0,
                        qtyShipped: parseInt(item.qtyShipped) || 0,
                        maxShip: Math.max(0, (parseInt(item.qtyOrdered) || 0) - (parseInt(item.qtyShipped) || 0)),
                        qtyShip: 0
                    }))
                })
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to fetch order details',
                    confirmButtonColor: '#d33'
                })
            }
        } catch (error) {
            console.error('Error fetching order details:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error fetching order details',
                confirmButtonColor: '#d33'
            })
        } finally {
            setLoading(false)
        }
    }

    const handleItemQuantityChange = (index, value) => {
        const newItems = [...shipmentForm.items]
        const quantity = parseInt(value) || 0
        newItems[index].qtyShip = Math.min(quantity, newItems[index].maxShip)
        
        const totalQty = newItems.reduce((sum, item) => sum + (item.qtyShip || 0), 0)
        
        setShipmentForm({
            ...shipmentForm,
            items: newItems,
            totalQty: totalQty
        })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        // Validate form
        const errors = ShipmentService.validateShipmentData(shipmentForm)
        if (errors.length > 0) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                html: errors.map(error => `• ${error}`).join('<br>'),
                confirmButtonColor: '#d33'
            })
            return
        }

        setSubmitting(true)

        try {
            const response = await ShipmentService.createShipment(shipmentForm)
            
            if (response.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Shipment created successfully!',
                    confirmButtonColor: '#3085d6',
                    timer: 2000,
                    showConfirmButton: false
                })
                history.push('/shipments')
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.errorMessage || 'Failed to create shipment',
                    confirmButtonColor: '#d33'
                })
            }
        } catch (error) {
            console.error('Error creating shipment:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error creating shipment',
                confirmButtonColor: '#d33'
            })
        } finally {
            setSubmitting(false)
        }
    }

    const getStatusBadge = (status) => {
        const statusMap = {
            'pending': 'badge-warning',
            'processing': 'badge-info',
            'confirm': 'badge-primary',
            'shipped': 'badge-success',
            'delivered': 'badge-success',
            'cancelled': 'badge-danger'
        }
        return statusMap[status] || 'badge-secondary'
    }

    const formatDate = (dateString) => {
        return ShipmentService.formatDate(dateString)
    }

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading order details...</p>
            </div>
        )
    }

    if (!order) {
        return (
            <div className="text-center py-5">
                <div className="text-muted">
                    <i className="fas fa-exclamation-triangle fa-3x mb-3"></i>
                    <p>Order not found</p>
                </div>
            </div>
        )
    }

    return (
        <div className="container-fluid">
            {/* Page Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <div className="d-flex align-items-center">
                        <Link to="/shipments" className="btn btn-link p-0 me-3">
                            <i className="fas fa-arrow-left"></i>
                        </Link>
                        <div>
                            <h1 className="h3 mb-0">Create Shipment</h1>
                            <p className="text-muted mb-0">Order #{order.orderNo}</p>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="row">
                    {/* Left Column */}
                    <div className="col-lg-8">
                        {/* Order Information */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Order Information</h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <table className="table table-borderless">
                                            <tbody>
                                                <tr>
                                                    <td><strong>Order No:</strong></td>
                                                    <td>{order.orderNo}</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Order Date:</strong></td>
                                                    <td>{formatDate(order.createdAt)}</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Status:</strong></td>
                                                    <td>
                                                        <span className={`badge ${getStatusBadge(order.status)}`}>
                                                            {order.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Grand Total:</strong></td>
                                                    <td>{ShipmentService.formatCurrency(order.grandTotal)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="col-md-6">
                                        <table className="table table-borderless">
                                            <tbody>
                                                <tr>
                                                    <td><strong>Customer:</strong></td>
                                                    <td>{order.customer?.fullName}</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Email:</strong></td>
                                                    <td>{order.customer?.email}</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Phone:</strong></td>
                                                    <td>{order.customer?.mobileNo}</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Shipping Method:</strong></td>
                                                    <td>{order.shipping_method?.methodName || 'N/A'}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Shipment Items */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Shipment Items</h5>
                            </div>
                            <div className="card-body">
                                {shipmentForm.items.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-striped">
                                            <thead>
                                                <tr>
                                                    <th>Product</th>
                                                    <th>SKU</th>
                                                    <th>Ordered Qty</th>
                                                    <th>Shipped Qty</th>
                                                    <th>Available Qty</th>
                                                    <th>Ship Qty</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {shipmentForm.items.map((item, index) => (
                                                    <tr key={item.orderItemId}>
                                                        <td>
                                                            <div>
                                                                <strong>{item.name}</strong>
                                                            </div>
                                                        </td>
                                                        <td>{item.sku}</td>
                                                        <td>
                                                            <span className="badge bg-primary">
                                                                {item.qtyOrdered}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-info">
                                                                {item.qtyShipped}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-success">
                                                                {item.maxShip}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                style={{ width: '80px' }}
                                                                min="0"
                                                                max={item.maxShip}
                                                                value={item.qtyShip}
                                                                onChange={(e) => handleItemQuantityChange(index, e.target.value)}
                                                                disabled={item.maxShip <= 0}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-4 text-muted">
                                        <i className="fas fa-boxes fa-3x mb-3"></i>
                                        <p>No items available for shipment</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Customer Notes */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Shipment Comments</h5>
                            </div>
                            <div className="card-body">
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    value={shipmentForm.customerNote}
                                    onChange={(e) => setShipmentForm({...shipmentForm, customerNote: e.target.value})}
                                    placeholder="Enter any comments about this shipment..."
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="col-lg-4">
                        {/* Shipment Summary */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Shipment Summary</h5>
                            </div>
                            <div className="card-body">
                                <table className="table table-borderless">
                                    <tbody>
                                        <tr>
                                            <td>Total Items:</td>
                                            <td className="text-end">{shipmentForm.items.filter(item => item.qtyShip > 0).length}</td>
                                        </tr>
                                        <tr>
                                            <td>Total Quantity:</td>
                                            <td className="text-end">{shipmentForm.totalQty}</td>
                                        </tr>
                                        <tr className="border-top">
                                            <th>Order Total:</th>
                                            <th className="text-end">{ShipmentService.formatCurrency(order.grandTotal)}</th>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Shipping Address */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Shipping Address</h5>
                            </div>
                            <div className="card-body">
                                {order.salesOrderShippingAddress ? (
                                    <div className="small">
                                        <div>{order.salesOrderShippingAddress.fullName}</div>
                                        <div>{order.salesOrderShippingAddress.street}</div>
                                        <div>{order.salesOrderShippingAddress.city}, {order.salesOrderShippingAddress.region}</div>
                                        <div>Phone: {order.salesOrderShippingAddress.phoneNumber}</div>
                                    </div>
                                ) : (
                                    <div className="text-muted">No shipping address</div>
                                )}
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="d-grid">
                            <button
                                type="submit"
                                className="btn btn-primary btn-lg"
                                disabled={submitting || shipmentForm.totalQty === 0}
                            >
                                {submitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Creating Shipment...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-shipping-fast me-2"></i>
                                        Create Shipment
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    )
}

export default ShipmentCreate 